import type { ModuleCode, PermissionCode } from '../../entities/me/me-contract.types';
import { ROUTE_PATHS } from '../../app/router/route-paths';
import { toFrontendStaffPath } from '../../shared/access/route-bridge';
import { STAFF_SPA_ROUTE_PATHS, type StaffSPARoutePath } from '../../shared/access/staff-spa-route-paths';

/** Группы сайдбара (стабильные id, не пользовательский текст). */
export type StaffNavSectionId =
  | 'shell'
  | 'organization'
  | 'users'
  | 'service_catalog'
  /** Залы/пространства и оборудование — ресурсы площадки, не только фитнес. */
  | 'resources'
  | 'subscriptions'
  | 'schedule'
  | 'payments'
  /** Настройка шлюзов и аудит платежей тенанта (`branches:org_write` / lift СА в org-context), не операционный реестр оплат. */
  | 'payment_governance'
  | 'application'
  | 'clients'
  | 'specialists'
  | 'fitness'
  | 'sales'
  /** Канбан-трекер задач организации (модуль `tasks`). */
  /** ИИ-помощник: история диалогов (до лимита тарифа) и чат; плавающее окно — облако-кнопка в shell. */
  | 'assistant'
  | 'tasks'
  | 'automation'
  | 'journal'
  | 'audit'
  | 'yclients'
  | 'platform'
  | 'account'
  | 'access_control';

export type StaffNavCatalogEntry = {
  frontendPath: string;
  backendRouteKey: StaffSPARoutePath;
  /** Org-модуль для UI `ModuleGate` (не для платежной оси — см. контракт). */
  moduleCode?: ModuleCode;
  /** Дополнительное право; если задано — узкий гейт поверх `routes_allowed`. */
  permissionCode?: PermissionCode;
  label: string;
  section: StaffNavSectionId;
  /**
   * Левое меню: по умолчанию пункт виден при `canShowNavItem`.
   * `false` — только deep link / будущий экран; маршрут по-прежнему в `routes_allowed` на бэкенде.
   * См. `docs/review/superadmin-navigation-product-audit.md` (план vs коммерческий тариф).
   */
  sidebarVisible?: boolean;
};

type NavMeta = Omit<StaffNavCatalogEntry, 'frontendPath' | 'backendRouteKey'>;

/**
 * Локальные подписи и секции; `moduleCode` — только где есть `staffRouteRequiredModule` в Go.
 * Полнота ключей проверяется типом `Record<StaffSPARoutePath, NavMeta>`.
 */
const NAV_META: Record<StaffSPARoutePath, NavMeta> = {
  '/': { label: 'Главная', section: 'shell' },
  '/superadmin/dashboard': {
    label: 'Платформа: сводка',
    section: 'platform',
    permissionCode: 'platform:dashboard:view'
  },
  '/superadmin/platform-users': {
    label: 'Пользователи платформы',
    section: 'platform',
    permissionCode: 'platform:users:view',
    sidebarVisible: false
  },
  '/superadmin/clients-catalog': {
    label: 'Клиенты организаций',
    section: 'platform',
    permissionCode: 'platform:organizations:view',
    sidebarVisible: false
  },
  '/superadmin/users': {
    label: 'Пользователи',
    section: 'platform'
  },
  '/superadmin/users/platform': {
    label: 'Пользователи — персонал платформы',
    section: 'platform',
    permissionCode: 'platform:users:view',
    sidebarVisible: false
  },
  '/superadmin/users/clients': {
    label: 'Пользователи — клиенты организаций',
    section: 'platform',
    permissionCode: 'platform:organizations:view',
    sidebarVisible: false
  },
  '/superadmin/users/roles': {
    label: 'Пользователи — роли платформы',
    section: 'platform',
    permissionCode: 'platform:superadmin',
    sidebarVisible: false
  },
  '/superadmin/payments': {
    label: 'Платежи платформы',
    section: 'platform',
    permissionCode: 'platform:billing:view'
  },
  '/tenant/dashboard': {
    label: 'Сводка организации',
    section: 'shell',
    /** Тот же экран, что и вход с «Главная» (`/`); пункт в сайдбаре не дублируем. */
    sidebarVisible: false
  },
  '/fitness/dashboard': {
    label: 'Фитнес: сводка',
    section: 'fitness',
    moduleCode: 'fitness',
    /** Редирект на `/staff/dashboard`; пункт не показываем в сайдбаре. */
    sidebarVisible: false
  },
  '/staff/dashboard': { label: 'Рабочий стол', section: 'shell' },
  '/organization': { label: 'Организация', section: 'organization' },
  '/organization/settings': {
    label: 'Настройки организации',
    section: 'organization',
    permissionCode: 'organization:settings:view',
    /** Единый surface — `/organization`; legacy URL только через редирект, не второй пункт меню. */
    sidebarVisible: false
  },
  '/organization/branches': { label: 'Филиалы', section: 'organization' },
  '/organization/services': {
    label: 'Услуги',
    section: 'organization',
    permissionCode: 'services:view',
    /** Единая точка входа — «Справочники» → «Услуги»; здесь только роут (открывается по ссылкам). */
    sidebarVisible: false
  },
  '/subscription': {
    label: 'Подписка',
    section: 'organization',
    permissionCode: 'subscription:view',
    sidebarVisible: false
  },
  '/services-types': { label: 'Типы услуг', section: 'service_catalog' },
  '/services-list': { label: 'Услуги', section: 'service_catalog' },
  '/services-spaces': { label: 'Залы и пространства', section: 'resources' },
  '/equipment': { label: 'Оборудование', section: 'resources', moduleCode: 'equipment' },
  '/subscriptions': {
    label: 'Абонементы',
    section: 'service_catalog',
    moduleCode: 'subscriptions',
    permissionCode: 'clients:org_read'
  },
  '/schedule': { label: 'Расписание', section: 'schedule' },
  '/schedule-workhours': { label: 'Рабочие часы', section: 'schedule', permissionCode: 'schedule:staff_write' },
  '/bookings': { label: 'Мои записи', section: 'schedule' },
  '/clients-bookings': { label: 'Записи клиентов', section: 'schedule', permissionCode: 'bookings:staff_write' },
  '/payments': { label: 'Платежи', section: 'payments' },
  '/application': { label: 'Внешний вид', section: 'application' },
  '/global-widget-theme': { label: 'Глобальные настройки', section: 'application' },
  '/clients': { label: 'Клиенты организации', section: 'users', permissionCode: 'clients:org_read', sidebarVisible: false },
  /** Пункт скрыт из сайдбара: сценарий специалистов уходит в «Пользователи» (`/users`). */
  '/staff': { label: 'Специалисты', section: 'specialists', moduleCode: 'fitness', sidebarVisible: false },
  '/sales-funnel': { label: 'Воронка продаж', section: 'sales', moduleCode: 'sales_funnel' },
  '/sales-funnel-settings': { label: 'Настройки воронки', section: 'sales', moduleCode: 'sales_funnel' },
  '/automation-sources': { label: 'Источники автоматизации', section: 'automation', moduleCode: 'automation' },
  '/automation-incoming': { label: 'Входящие (автоматизация)', section: 'automation', moduleCode: 'automation' },
  '/incoming-requests': {
    label: 'Входящие заявки',
    section: 'automation',
    moduleCode: 'automation',
    permissionCode: 'service_requests:org'
  },
  /**
   * Журнал действий: API/маршрут могут быть в снимке тенанта, но в UI пункт только в контуре платформы (см. `staff-sidebar` filter).
   * `permissionCode` — не опираться на `audit:org_read` для показа в меню.
   */
  '/audit-log': { label: 'Журнал действий', section: 'audit', permissionCode: 'platform:audit:view' },
  '/audit-payments': { label: 'Аудит платежей', section: 'audit' },
  '/yc-sync-summary': { label: 'YClients: сводка', section: 'yclients', moduleCode: 'yclients' },
  '/yc-sync-settings': {
    label: 'Синхронизация YClients',
    section: 'yclients',
    moduleCode: 'yclients'
  },
  '/yc-discover-branches': {
    label: 'YClients: компании → филиалы',
    section: 'yclients',
    moduleCode: 'yclients',
    sidebarVisible: false
  },
  '/yc-sync-log': { label: 'YClients: журнал', section: 'yclients', moduleCode: 'yclients', sidebarVisible: false },
  '/yc-sync-conflicts': { label: 'YClients: конфликты', section: 'yclients', moduleCode: 'yclients', sidebarVisible: false },
  '/yc-unmapped': { label: 'YClients: без сопоставления', section: 'yclients', moduleCode: 'yclients' },
  '/pending-auth': {
    label: 'Ожидающие авторизации',
    /**
     * Секция `audit`, а не `platform`: очередь видна и в tenant-shell (СА в контексте org,
     * org-staff с automation), и в platform-shell (whitelist). `platform` скрывал бы пункт
     * в tenant-меню (см. `staff-sidebar` filter).
     */
    section: 'audit'
  },
  /** Пункт ведёт в футер сайдбара; на `/docs` левая колонка — список документов. */
  '/docs': { label: 'Документация', section: 'shell', sidebarVisible: false },
  '/contact': { label: 'Оставить отзыв', section: 'shell', sidebarVisible: false },
  '/feedback': { label: 'Клиентский портал', section: 'account' },
  '/payment-gateways': { label: 'Платёжные шлюзы', section: 'organization' },
  '/admin-tokens': { label: 'Токены для интеграций', section: 'platform' },
  '/settings': {
    label: 'Настройки CRM',
    /** Платформа: только СА без org-context; в tenant-меню тот же ключ ведёт на org-side экран `/settings`. */
    section: 'platform'
  },
  '/platform-plans': {
    label: 'Тарифные планы',
    section: 'platform',
    permissionCode: 'platform:plans:view'
  },
  '/tariffs': { label: 'Тариф', section: 'platform' },
  '/billing': {
    label: 'История счетов',
    section: 'organization',
    /** Пункт скрыт из сайдбара: вход — кнопка «История счетов» в разделе «Тариф» (`/tariffs`). */
    sidebarVisible: false
  },
  '/all-users': {
    label: 'Пользователи (старый адрес)',
    section: 'users',
    permissionCode: 'users:org_directory',
    sidebarVisible: false
  },
  '/users': { label: 'Пользователи', section: 'users' },
  /** Пункт скрыт из сайдбара: сценарий в хабе «Пользователи» (`/users/access-rbac`). */
  '/access-rbac': { label: 'Доступы и роли', section: 'users', moduleCode: 'admin_rbac', sidebarVisible: false },
  '/platform-organizations': {
    label: 'Организации',
    section: 'platform',
    permissionCode: 'platform:organizations:view'
  },
  '/platform-tenant-requests': {
    label: 'Заявки на подключение',
    section: 'platform',
    permissionCode: 'platform:tenant-requests:view'
  },
  '/widget-embed-lab': { label: 'Предпросмотр', section: 'application' },
  '/fitness': { label: 'Fitness', section: 'fitness', moduleCode: 'fitness', sidebarVisible: false },
  '/qrcode': { label: 'Журнал проходов', section: 'access_control', moduleCode: 'qrcode' },
  '/qrcode-settings': { label: 'Настройки QR', section: 'access_control', moduleCode: 'qrcode' },
  '/platform-qrcode-config': { label: 'QR-доступ (глобальный)', section: 'platform' },
  '/tasks': { label: 'Задачи', section: 'tasks', moduleCode: 'tasks', permissionCode: 'tasks:read' },
  '/assistant': { label: 'Помощник', section: 'assistant', moduleCode: 'assistant', permissionCode: 'assistant:use' },
  '/superadmin/assistant': {
    label: 'ИИ-помощник',
    section: 'platform',
    permissionCode: 'platform:assistant:view'
  },
  '/me': { label: 'Профиль', section: 'account' }
};

function catalogFrontendPath(backendRouteKey: StaffSPARoutePath): string {
  if (backendRouteKey === '/superadmin/users') {
    return '/superadmin/users';
  }
  if (backendRouteKey === '/access-rbac') {
    return ROUTE_PATHS.tenantUsersAccessRbacTab;
  }
  return toFrontendStaffPath(backendRouteKey);
}

function buildCatalog(): StaffNavCatalogEntry[] {
  return STAFF_SPA_ROUTE_PATHS.map((backendRouteKey) => {
    const meta = NAV_META[backendRouteKey];
    return {
      frontendPath: catalogFrontendPath(backendRouteKey),
      backendRouteKey,
      ...meta
    };
  });
}

/** Каталог пунктов навигации (порядок как в `STAFF_SPA_ROUTE_PATHS`). */
export const STAFF_NAV_CATALOG: StaffNavCatalogEntry[] = buildCatalog();

/**
 * Секция сайдбара для группировки: `/feedback` в режиме платформы — в группе «Платформа».
 * Аудит (`/audit-log`, `/audit-payments`) — секция `audit` (между «Платформа» и «Приложение» в порядке сайдбара).
 */
export function resolveStaffNavSidebarSection(
  entry: StaffNavCatalogEntry,
  platformMode: boolean
): StaffNavSectionId {
  if (entry.backendRouteKey === '/feedback' && platformMode) return 'platform';
  if (entry.backendRouteKey === '/settings' && !platformMode) return 'organization';
  if (entry.backendRouteKey === '/tariffs' && !platformMode) return 'organization';
  return entry.section;
}

/** Порядок в секции «Пользователи» (в сайдбаре только `/users`; остальное — вкладки хаба). */
const USERS_SECTION_SIDEBAR_ORDER: readonly StaffSPARoutePath[] = ['/users'];

/** Порядок в секции «Организация» для тенанта: карточка, филиалы, настройки компании (ИНН, ОГРН и т.д.), тариф. */
const ORGANIZATION_SECTION_SIDEBAR_ORDER: readonly StaffSPARoutePath[] = [
  '/organization',
  '/organization/branches',
  '/settings',
  '/payment-gateways',
  '/tariffs'
];

export function orderStaffNavItemsForSidebarSection(
  sectionId: StaffNavSectionId,
  items: StaffNavCatalogEntry[]
): StaffNavCatalogEntry[] {
  if (sectionId === 'users') {
    const pri = new Map(USERS_SECTION_SIDEBAR_ORDER.map((k, i) => [k, i]));
    return [...items].sort((a, b) => {
      const pa = pri.get(a.backendRouteKey) ?? 1000;
      const pb = pri.get(b.backendRouteKey) ?? 1000;
      if (pa !== pb) return pa - pb;
      return STAFF_SPA_ROUTE_PATHS.indexOf(a.backendRouteKey) - STAFF_SPA_ROUTE_PATHS.indexOf(b.backendRouteKey);
    });
  }
  if (sectionId === 'organization') {
    const pri = new Map(ORGANIZATION_SECTION_SIDEBAR_ORDER.map((k, i) => [k, i]));
    return [...items].sort((a, b) => {
      const pa = pri.get(a.backendRouteKey) ?? 1000;
      const pb = pri.get(b.backendRouteKey) ?? 1000;
      if (pa !== pb) return pa - pb;
      return STAFF_SPA_ROUTE_PATHS.indexOf(a.backendRouteKey) - STAFF_SPA_ROUTE_PATHS.indexOf(b.backendRouteKey);
    });
  }
  return items;
}
